import React from 'react';
import { getMaterialLabel, getStageLabel, getStoriesLabel } from '../utils/pricing/formatting';

const SERVICE_ORDER = ['roof', 'siding', 'gutter', 'driveway', 'deck', 'fence'];

/**
 * Default branding fallbacks (used when no branding prop provided).
 */
const DEFAULT_BRANDING = {
  companyName: 'Your Company',
  tagline: '',
  logoUrl: null,
  address: { line1: '', city: '', state: '', zip: '' },
  phone: '',
  website: '',
  colors: { primary: '#f26b1d', secondary: '#0f4c81' },
};

/**
 * Format address object into display strings.
 */
const formatAddressLine = (address) => {
  if (!address) return '';
  return address.line1 || '';
};

const formatCityStateZip = (address) => {
  if (!address) return '';
  const parts = [];
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zip) parts.push(address.zip);
  if (parts.length === 0) return '';
  // Format: "City, State Zip" or "City State" etc.
  if (address.city && address.state) {
    return `${address.city}, ${address.state} ${address.zip || ''}`.trim();
  }
  return parts.join(' ');
};

/**
 * PrintableQuote - Renders a print-friendly quote with dynamic company branding.
 * 
 * Branding is sourced from companies/{companyId}/settings/branding.
 * All hardcoded references to specific companies have been removed.
 * 
 * @param {Object} props.branding - Company branding settings (from useCompanyBranding hook)
 * @param {Object} props.clientInfo - Client/customer information
 * @param {Object} props.services - Service calculations (roof, siding, etc.)
 * @param {Object} props.totals - Quote totals (subtotal, tax, grandTotal)
 */
export const PrintableQuote = React.forwardRef(({ 
  branding: brandingProp, 
  clientInfo, 
  services = {}, 
  totals 
}, ref) => {
  // Merge provided branding with defaults
  const branding = {
    ...DEFAULT_BRANDING,
    ...brandingProp,
    address: { ...DEFAULT_BRANDING.address, ...brandingProp?.address },
    colors: { ...DEFAULT_BRANDING.colors, ...brandingProp?.colors },
  };

  const { primary, secondary } = branding.colors;
  const addressLine = formatAddressLine(branding.address);
  const cityStateZip = formatCityStateZip(branding.address);
  const fullAddress = [addressLine, cityStateZip].filter(Boolean).join(', ');

  // Build contact line: phone • website
  const contactParts = [];
  if (branding.phone) contactParts.push(branding.phone);
  if (branding.website) contactParts.push(branding.website);
  const contactLine = contactParts.join(' • ');

  return (
    <div ref={ref} className="print-only" style={{ display: 'none' }}>
      <style>{`
        @media print {
          .print-only {
            display: block !important;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .print-hidden {
            display: none !important;
          }
          body {
            background: white !important;
          }
          @page {
            margin: 0.5in;
            size: letter;
          }
        }
      `}</style>
      
      <div style={{ 
        border: `3px solid ${primary}`,
        borderRadius: '12px',
        padding: '40px',
        backgroundColor: '#ffffff'
      }}>
        {/* Header - Dynamic Branding */}
        <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: `2px solid ${primary}`, paddingBottom: '20px' }}>
          {branding.logoUrl && (
            <img 
              src={branding.logoUrl} 
              alt={`${branding.companyName} Logo`}
              style={{ height: '80px', marginBottom: '10px', maxWidth: '200px', objectFit: 'contain' }}
            />
          )}
          <h1 style={{ margin: '8px 0', color: secondary, fontSize: '28px', fontWeight: 'bold' }}>
            {branding.companyName}
          </h1>
          {branding.tagline && (
            <div style={{ fontSize: '16px', color: primary, fontWeight: '600', marginBottom: '8px' }}>
              {branding.tagline}
            </div>
          )}
          {fullAddress && (
            <div style={{ fontSize: '14px', color: '#444', marginTop: '8px' }}>{fullAddress}</div>
          )}
          {contactLine && (
            <div style={{ fontSize: '14px', color: '#444' }}>{contactLine}</div>
          )}
        </div>
        
        {/* Quote Title */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h2 style={{ fontSize: '24px', color: secondary, fontWeight: 'bold', margin: '0' }}>Service Quote</h2>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>Date: {new Date().toLocaleDateString()}</p>
        </div>
        
        {/* Client Information */}
        <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: '18px', color: secondary, marginBottom: '15px', fontWeight: 'bold' }}>Client Information</h3>
          <table style={{ width: '100%', fontSize: '14px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '6px 0', color: '#666', width: '120px' }}><strong>Name:</strong></td>
                <td style={{ padding: '6px 0', color: '#222' }}>{clientInfo.firstName} {clientInfo.lastName}</td>
              </tr>
              {clientInfo.address && (
                <tr>
                  <td style={{ padding: '6px 0', color: '#666' }}><strong>Address:</strong></td>
                  <td style={{ padding: '6px 0', color: '#222' }}>
                    {clientInfo.address}<br />
                    {clientInfo.city}{clientInfo.city && ', '}{clientInfo.state} {clientInfo.zip}
                  </td>
                </tr>
              )}
              {clientInfo.phone && (
                <tr>
                  <td style={{ padding: '6px 0', color: '#666' }}><strong>Phone:</strong></td>
                  <td style={{ padding: '6px 0', color: '#222' }}>{clientInfo.phone}</td>
                </tr>
              )}
              {clientInfo.email && (
                <tr>
                  <td style={{ padding: '6px 0', color: '#666' }}><strong>Email:</strong></td>
                  <td style={{ padding: '6px 0', color: '#222' }}>{clientInfo.email}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Job Details - show only if roof service present */}
        {services.roof && (
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: '18px', color: secondary, marginBottom: '15px', fontWeight: 'bold' }}>Roof Job Details</h3>
            <table style={{ width: '100%', fontSize: '14px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '6px 0', color: '#666', width: '180px' }}><strong>Roof Size:</strong></td>
                  <td style={{ padding: '6px 0', color: '#222' }}>
                    {services.roof.input?.sqFt} sq ft (Adjusted: {services.roof.result?.adjustedSqFt?.toFixed?.(0) || services.roof.result?.adjustedSqFt || 0} sq ft)
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '6px 0', color: '#666' }}><strong>Roof Material:</strong></td>
                  <td style={{ padding: '6px 0', color: '#222' }}>{getMaterialLabel(services.roof.input?.material)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px 0', color: '#666' }}><strong>Contamination Stage:</strong></td>
                  <td style={{ padding: '6px 0', color: '#222' }}>{getStageLabel(services.roof.input?.stage)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px 0', color: '#666' }}><strong>Roof Pitch:</strong></td>
                  <td style={{ padding: '6px 0', color: '#222' }}>{services.roof.input?.pitch}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px 0', color: '#666' }}><strong>Building Height:</strong></td>
                  <td style={{ padding: '6px 0', color: '#222' }}>{getStoriesLabel(services.roof.input?.stories)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pricing - Multi-service breakdown */}
        <div style={{ marginBottom: '30px' }}>
          <table style={{ width: '100%', fontSize: '16px', borderCollapse: 'collapse' }}>
            <tbody>
              {SERVICE_ORDER.map((key) => {
                const svc = services[key];
                const total = svc?.result?.grandTotal;
                if (!svc || !svc.result || !Number(total)) return null;

                const labelMap = {
                  roof: 'Roof Cleaning Service',
                  siding: 'Siding Cleaning Service',
                  gutter: 'Gutter Cleaning Service',
                  driveway: 'Driveway / Walkway Service',
                  deck: 'Deck / Patio Service',
                  fence: 'Fence Cleaning Service',
                };

                return (
                  <tr key={key}>
                    <td style={{ padding: '12px 0', color: '#666', borderBottom: '1px solid #e2e8f0' }}>{labelMap[key]}</td>
                    <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 'bold', color: '#222', borderBottom: '1px solid #e2e8f0' }}>
                      ${Number(total).toFixed(2)}
                    </td>
                  </tr>
                );
              })}

              {totals?.discount > 0 && (
                <tr>
                  <td style={{ padding: '12px 0', color: '#059669', borderBottom: '1px solid #e2e8f0' }}>Discount</td>
                  <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 'bold', color: '#059669', borderBottom: '1px solid #e2e8f0' }}>
                    -${Number(totals.discount).toFixed(2)}
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ padding: '12px 0', color: '#666', borderBottom: `2px solid ${secondary}` }}>Tax</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 'bold', color: '#222', borderBottom: `2px solid ${secondary}` }}>
                  ${Number(totals?.tax || 0).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '16px 0', fontSize: '20px', fontWeight: 'bold', color: secondary }}>TOTAL</td>
                <td style={{ padding: '16px 0', textAlign: 'right', fontSize: '28px', fontWeight: 'bold', color: secondary }}>
                  ${Number(totals?.grandTotal || 0).toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Notes */}
        <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#fef3e7', borderRadius: '8px', border: `1px solid ${primary}` }}>
          <h4 style={{ fontSize: '16px', color: secondary, marginBottom: '10px', fontWeight: 'bold' }}>Service Notes:</h4>
          <ul style={{ fontSize: '13px', color: '#444', lineHeight: '1.8', paddingLeft: '20px', margin: '0' }}>
            <li>All work performed using eco-friendly soft wash techniques</li>
            <li>Quote valid for 30 days from date of issue</li>
            <li>Payment due upon completion of service</li>
            <li>Satisfaction guaranteed</li>
          </ul>
        </div>
        
        {/* Footer - Dynamic Company Name */}
        <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: `2px solid ${primary}`, textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#666', margin: '0' }}>
            Thank you for choosing {branding.companyName}!
          </p>
          <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
            Powered by <strong style={{ color: primary }}>KRO</strong>
          </p>
        </div>
      </div>
    </div>
  );
});

PrintableQuote.displayName = 'PrintableQuote';

export default PrintableQuote;
